<script setup lang="ts">
// NPM
import { reactive, ref, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import {
  createI18nMessage,
  helpers,
  maxLength,
  required,
} from '@vuelidate/validators';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { zodEmailValidator } from '@/utils/emailValidation';

// Components
import {
  FieldAvatarUploader,
  FieldInput,
  FieldPhoneNumber,
  FieldSelect,
  BaseButton,
} from '@magma-app/magma-lapilli';
import AddressAutocomplete, {
  type SelectedAddress,
} from '@/components/fields/AddressAutocomplete.vue';

// Config
import { useLanguageOptions } from '@/components/ambassador-signup/config/languages-config';

// Stores
import { useUserStore } from '@/stores/user';
import { useCommonStore } from '@/stores/common';
import { useAsyncAction } from '@/composables/useAsyncAction';
const userStore = useUserStore();
const commonStore = useCommonStore();
const { isRunning: isSaving, run: runSave } = useAsyncAction();
const { run: runUpload } = useAsyncAction();

// Plugins
const { t, locale } = useI18n({ useScope: 'global' });
const withI18nMessage = createI18nMessage({ t });

// Reactive variables
const formData = reactive({
  profilePicture: null,
  firstname: userStore.user?.firstname || '',
  lastname: userStore.user?.lastname || '',
  email: userStore.user?.email || '',
  phoneNumber: userStore.user?.phoneNumber || '',
  language: userStore.user?.language || locale.value || 'en',
  textNotifications: userStore.user?.acceptPhoneNotifications || undefined,
  emailNotifications: userStore.user?.acceptEmailNotifications || undefined,
  personalEmail: userStore.user?.email || '',
  position: userStore.user?.position || '',
  languagesSpoken: Array.isArray(userStore.user?.languagesSpoken)
    ? userStore.user.languagesSpoken
    : userStore.user?.languagesSpoken
      ? [userStore.user.languagesSpoken]
      : [],
  address: userStore.user?.address || '',
  googlePlaceId: userStore.user?.googlePlaceId ?? null,
});

const { getSortedLanguageOptions } = useLanguageOptions();

// Language options for spoken languages from config
const languagesSpokenOptions = getSortedLanguageOptions().map((lang) => ({
  label: lang.label,
  value: lang.value,
}));

// Computed property to check if phone number should be disabled
const isPhoneNumberDisabled = computed(() => {
  return !!(
    userStore.user?.phoneNumber && userStore.user.phoneNumber.trim().length > 0
  );
});
const formDataSaved = ref(JSON.stringify(formData));

// Form validation
const POSITION_MAX_LENGTH = 600;

const v$ = useVuelidate(
  {
    firstname: {
      required,
    },
    lastname: {
      required,
    },
    personalEmail: {
      email: zodEmailValidator,
    },
    phoneNumber: {
      required: withI18nMessage(required, {
        messagePath: () => t('generic.phoneNumber') + t('validation.required'),
      }),
      validPhoneNumber: withI18nMessage(
        (value: string) => {
          // Skip validation if field is disabled and already has a value
          if (isPhoneNumberDisabled.value && userStore.user?.phoneNumber) {
            return true;
          }
          return value?.length != 0 ? isValidPhoneNumber(value) : true;
        },
        {
          messagePath: () => t('generic.phoneNumber') + t('validation.valid'),
        }
      ),
    },
    // users.position is varchar(600); the backend rejects anything longer
    position: {
      maxLength: withI18nMessage(maxLength(POSITION_MAX_LENGTH), {
        messagePath: () => 'validation.maxLength600',
      }),
    },
    profilePicture: {
      validSize: helpers.withMessage(
        `${t('input.rules.picture.size')} 9MB`,
        (value: File) => {
          if (value) {
            return value ? value.size < 9000000 : true;
          } else {
            return true;
          }
        }
      ),
    },
  },
  formData
);

// Methods
const hasChanges = () => {
  return formDataSaved.value !== JSON.stringify(formData);
};

const getChangedFields = () => {
  const savedData = JSON.parse(formDataSaved.value);
  const changedFields: { [key: string]: string | boolean | string[] | null } =
    {};

  // Map form fields to API field names
  const fieldMapping: { [key: string]: string } = {
    firstname: 'firstname',
    lastname: 'lastname',
    personalEmail: 'notificationEmail',
    textNotifications: 'acceptPhoneNotifications',
    emailNotifications: 'acceptEmailNotifications',
    position: 'position',
    language: 'language',
    languagesSpoken: 'languagesSpoken',
    address: 'address',
    googlePlaceId: 'googlePlaceId',
  };

  // Only include phoneNumber if it's not disabled
  if (!isPhoneNumberDisabled.value) {
    fieldMapping.phoneNumber = 'phoneNumber';
  }

  // Compare each field and add only changed ones
  Object.entries(fieldMapping).forEach(([formField, apiField]) => {
    const currentValue = formData[formField as keyof typeof formData];
    const savedValue = savedData[formField];

    // Special handling for array fields (like languagesSpoken)
    let hasChanged = false;
    if (Array.isArray(currentValue) && Array.isArray(savedValue)) {
      // For arrays, compare lengths and elements
      hasChanged =
        currentValue.length !== savedValue.length ||
        !currentValue.every((item, index) => item === savedValue[index]);
    } else {
      // For non-array fields, simple comparison
      hasChanged = currentValue !== savedValue;
    }

    if (hasChanged && currentValue !== undefined) {
      changedFields[apiField] = currentValue;
    }
  });

  return changedFields;
};

const saveProfile = () => runSave(async () => {
  v$.value.$touch();

  if ((await v$.value.$validate()) && hasChanges()) {
    try {
      const changedFields = getChangedFields();

      // Only make API call if there are actual changes
      if (Object.keys(changedFields).length > 0) {
        await userStore.updateUser(changedFields);

        // Update locale if language changed
        if (
          changedFields.language &&
          typeof changedFields.language === 'string' &&
          changedFields.language !== locale.value
        ) {
          locale.value = changedFields.language;
        }

        formDataSaved.value = JSON.stringify(formData);

        // Show success toast notification
        commonStore.setAlert({
          open: true,
          type: 'success',
          title: t('generic.informationUpdated'),
        });
      }
    } catch (error) {
      console.log(error);
    }
  }
});

const uploadProfilePicture = (profilePicture: File) => runUpload(async () => {
  if (await v$.value.profilePicture.$validate()) {
    await userStore.uploadPicture({
      profilePicture,
    });

    // Show success toast notification for profile picture upload
    commonStore.setAlert({
      open: true,
      type: 'success',
      title: t('generic.informationUpdated'),
    });
  }
});

const handleAddressInput = (value: string) => {
  formData.address = value;
  formData.googlePlaceId = null;
};

const handleAddressSelect = (selection: SelectedAddress) => {
  formData.address = selection.formattedAddress;
  formData.googlePlaceId = selection.placeId;
};
</script>

<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-row gap-8 items-start justify-between">
      <h2 class="text-2xl font-bold leading-7 text-foreground-emphasis w-2/3">
        {{ $t('settingsProfileView.profileHeading') }}
      </h2>
      <!-- IF THERE'S NO MODIFICATIONS: DISABLE THE BUTTON -->
      <BaseButton
        :text="$t('generic.save')"
        size="md"
        class="w-1/3"
        :disabled="isSaving || !hasChanges()"
        @click="saveProfile"
      />
    </div>
    <div class="flex flex-col gap-4">
      <FieldAvatarUploader
        id="profilePicture"
        v-model="formData.profilePicture"
        :cta="$t('generic.change')"
        :label="$t('generic.profilePicture')"
        :locale="$i18n.locale"
        :preview-url="userStore.user?.profilePictureUrl || undefined"
        :validation="v$.profilePicture"
        @change="uploadProfilePicture"
      />
      <FieldInput
        id="firstname"
        v-model="formData.firstname"
        :locale="$i18n.locale"
        :label="$t('generic.firstname')"
        :validation="v$.firstname"
      />
      <FieldInput
        id="lastname"
        v-model="formData.lastname"
        :locale="$i18n.locale"
        :label="$t('generic.lastname')"
        :validation="v$.lastname"
      />
      <FieldInput
        id="email"
        v-model="formData.email"
        role="email"
        type="email"
        :locale="$i18n.locale"
        :label="$t('settingsProfileView.accountEmail')"
        disabled
      />
      <FieldPhoneNumber
        id="phone"
        v-model="formData.phoneNumber"
        :locale="$i18n.locale"
        :label="$t('generic.phoneNumber')"
        :validation="v$.phoneNumber"
        :disabled="isPhoneNumberDisabled"
      />
      <div class="relative">
        <span
          :class="[
            'absolute right-0 text-mgm-txt-sm',
            formData.position.length > POSITION_MAX_LENGTH
              ? 'text-foreground-danger-default'
              : 'text-foreground-default'
          ]"
        >
          {{ formData.position.length }} / {{ POSITION_MAX_LENGTH }}
        </span>
        <FieldInput
          id="text"
          v-model="formData.position"
          :locale="$i18n.locale"
          :label="$t('generic.position')"
          :validation="v$.position"
        />
      </div>
      <FieldSelect
        id="languagesSpoken"
        v-model="formData.languagesSpoken"
        :locale="$i18n.locale"
        :label="$t('helperOnboarding.profile.languagesLabel')"
        :placeholder="$t('helperOnboarding.profile.languagesPlaceholder')"
        :options="languagesSpokenOptions"
        multiple
      />
      <AddressAutocomplete
        id="address"
        :model-value="formData.address || ''"
        :place-id="formData.googlePlaceId"
        :label="$t('helperOnboarding.postalAddress.label')"
        :placeholder="$t('helperOnboarding.postalAddress.placeholder')"
        @update:model-value="handleAddressInput"
        @select="handleAddressSelect"
      />
    </div>
  </div>
</template>
